// Geteilter Fortschritts-Helper fuer die 4 Foto-Sektionen (Exterior, Felgen,
// Reifen, Interior). Schaeden ist BEWUSST nicht enthalten: der Kamera-Test wird
// nur bis zum Abschluss dieser 4 Sektionen abgefragt.
//
// "done" muss identisch zur kanonischen Schritt-Logik in fetchVehicleSteps.js
// (checkVehicleSteps) sein, sonst routet/gated der Kamera-Test falsch:
//   - count = Anzahl vorhandener carImages-Keys (nur fuer "schon irgendein Foto?")
//   - done  = alle Pflichtfotos der Sektion vorhanden (Interior: vordersitze +
//             armaturenbrett + cockpit immer; ruecksitzbank + kofferraum nur,
//             wenn nicht via vehicleFeatures ausgenommen)
//
// Reihenfolge wie in der Uebersicht (FotosLeftSide) und im Flow
// (getNextPhotosPath): exterior -> felgen -> reifen -> interior.

const SECTIONS = [
  {
    key: "exterior",
    label: "Exterior",
    segment: "exterior",
    keys: [
      "vordereFahrerecke",
      "vordereBeifahrerecke",
      "beifahrerseiteHinten",
      "fahrerseiteHinten",
    ],
  },
  {
    key: "felgen",
    label: "Felgen",
    segment: "felgen",
    keys: [
      "vordereFahrerfelge",
      "vordereBeifahrerfelge",
      "hintereFahrerfelge",
      "hintereBeifahrerfelge",
    ],
  },
  {
    key: "reifen",
    label: "Reifenprofil",
    segment: "reifen",
    keys: [
      "vordererFahrerreifen",
      "vordererBeifahrerreifen",
      "hintererBeifahrerreifen",
      "hintererFahrerreifen",
    ],
  },
  {
    key: "interior",
    label: "Interior",
    segment: "interior",
    keys: ["vordersitze", "armaturenbrett", "cockpit", "rücksitzbank", "kofferraum"],
  },
];

function imageKeyPresent(carImages, key) {
  return Boolean(
    carImages?.[key] ||
      carImages?.[`${key}_webp`] ||
      carImages?.[`${key}_jpeg`],
  );
}

// Interior-Sektion fertig — EINZIGE Quelle fuer alle FE-Konsumenten (isSectionDone,
// fetchVehicleSteps, Fotosschiessen) UND deckungsgleich zur Backend-Regel
// isInteriorPhotosComplete: vordersitze/armaturenbrett/cockpit immer,
// ruecksitzbank + kofferraum UNABHAENGIG exemptiert (jeweils eigenes
// vehicleFeatures-Flag ODER Foto). Alle Alias-Schreibweisen (Umlaut/ue/u).
export function isInteriorComplete(carImages, vehicleFeatures) {
  const f = vehicleFeatures || {};
  const has = (k) => imageKeyPresent(carImages, k);
  const mandatory = has("vordersitze") && has("armaturenbrett") && has("cockpit");
  const rearSeatDone =
    f.rücksitzbank === true ||
    f.ruecksitzbank === true ||
    f.rucksitzbank === true ||
    has("rücksitzbank") ||
    has("ruecksitzbank") ||
    has("rucksitzbank");
  const trunkDone = Boolean(f.kofferraum) || has("kofferraum");
  return mandatory && rearSeatDone && trunkDone;
}

// Soll-Anzahl Interior-Fotos (fuer die "X von Y erledigt"-Anzeige): 3 Pflicht
// plus je 1 fuer nicht-exemptierte Ruecksitzbank/Kofferraum (unabhaengig).
export function interiorRequiredTotal(vehicleFeatures) {
  const f = vehicleFeatures || {};
  const rearExempt =
    f.rücksitzbank === true || f.ruecksitzbank === true || f.rucksitzbank === true;
  const trunkExempt = Boolean(f.kofferraum);
  return 3 + (rearExempt ? 0 : 1) + (trunkExempt ? 0 : 1);
}

// Anzahl der PFLICHT-relevanten Interior-Fotos, die vorhanden sind (exemptierte
// Optional-Fotos zaehlen nicht mit) — Zaehler fuer "X von Y".
export function interiorPresentCount(carImages, vehicleFeatures) {
  const f = vehicleFeatures || {};
  const has = (k) => imageKeyPresent(carImages, k);
  let count = ["vordersitze", "armaturenbrett", "cockpit"].filter(has).length;
  const rearExempt =
    f.rücksitzbank === true || f.ruecksitzbank === true || f.rucksitzbank === true;
  const trunkExempt = Boolean(f.kofferraum);
  if (!rearExempt && (has("rücksitzbank") || has("ruecksitzbank") || has("rucksitzbank"))) {
    count += 1;
  }
  if (!trunkExempt && has("kofferraum")) count += 1;
  return count;
}

// Backend-aequivalentes "irgendein Profilfoto vorhanden?" (Spiegel von
// helpers/profileFlowV2.js hasAnyProfileImage): alle Sektions-Keys inkl. der
// ruecksitzbank/rucksitzbank-Aliasse UND _webp/_jpeg-Varianten PLUS Schaden-
// Bilder. Breiter als hasAnyPhotoSection (das nur die kanonischen Keys zaehlt) —
// noetig, damit die Reminder-Box eine Legacy-Erinnerung, die der Worker als
// photo_exists canceln wuerde, nicht faelschlich als aktiv anzeigt.
export function hasAnyProfileImageLike(carData) {
  const carImages = carData?.carImages || {};
  const regularKeys = [
    ...SECTIONS.flatMap((section) => section.keys),
    "ruecksitzbank",
    "rucksitzbank",
  ];
  if (regularKeys.some((key) => imageKeyPresent(carImages, key))) return true;
  return [
    carData?.carDamages,
    carData?.carDamagesWebp,
    carData?.carDamagesLocation,
    carData?.carDamagesLocationWebp,
  ].some(
    (map) =>
      map &&
      typeof map === "object" &&
      Object.values(map).some((value) => Boolean(value)),
  );
}

// "done" pro Sektion = alle Pflichtfotos vorhanden, EXAKT wie die kanonische
// Schritt-Logik in fetchVehicleSteps.js (checkVehicleSteps). Reines Zaehlen waere
// falsch: bei reduziertem Interior-Soll wuerden 3 beliebige Keys genuegen, obwohl
// vordersitze/armaturenbrett/cockpit Pflicht sind.
function isSectionDone(section, carData) {
  const carImages = carData?.carImages;
  if (!carImages) return false;
  const has = (key) => Boolean(carImages[key]);

  if (section.key === "interior") {
    return isInteriorComplete(carImages, carData?.vehicleFeatures);
  }

  // exterior / felgen / reifen: alle (genau 4) Keys muessen vorhanden sein.
  return section.keys.every(has);
}

// Status aller 4 Sektionen: [{ key, label, segment, count, done }].
// count = Anzahl vorhandener Keys (nur fuer "schon irgendein Foto gemacht?").
export function getFotosSectionStatuses(carData) {
  const carImages = carData?.carImages;
  return SECTIONS.map((section) => {
    const count = carImages
      ? section.keys.filter((key) => Boolean(carImages[key])).length
      : 0;
    return {
      key: section.key,
      label: section.label,
      segment: section.segment,
      count,
      done: isSectionDone(section, carData),
    };
  });
}

// Naechste unfertige Sektion (in Reihenfolge) + fertiger Pfad, oder null wenn
// alle 4 erledigt sind.
export function getNextIncompletePhotoSection(carData, id) {
  const next = getFotosSectionStatuses(carData).find((section) => !section.done);
  if (!next) return null;
  return {
    ...next,
    path: `/${id}/profil/fotos/fotos-schiessen/${next.segment}`,
  };
}

// Sind alle 4 Sektionen erledigt? Bei unbekanntem carData (noch nicht geladen)
// bewusst false -> der Kamera-Test wird nicht faelschlich uebersprungen.
export function areAllPhotoSectionsDone(carData) {
  if (!carData) return false;
  return getFotosSectionStatuses(carData).every((section) => section.done);
}

// Wurde in irgendeiner der 4 Sektionen schon mindestens ein Foto gemacht?
export function hasAnyPhotoSection(carData) {
  return getFotosSectionStatuses(carData).some((section) => section.count > 0);
}
